import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

/**
 * Minimal data loader: enough for an MVP where every screen is a single GET.
 * `deps` re-runs the fetch; `reload` forces it after a mutation. With
 * `keepDataOnReload` a re-fetch leaves the current data on screen instead of flashing
 * a skeleton — what a feed wants after you follow someone.
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: readonly unknown[],
  options: { keepDataOnReload?: boolean } = {},
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);
  const shown = useRef<T | null>(null);
  shown.current = data;

  useEffect(() => {
    const ticket = ++latest.current;
    const keepData = Boolean(options.keepDataOnReload) && shown.current !== null;
    if (!keepData) setLoading(true);
    setError(null);
    fn().then(
      (result) => {
        if (ticket !== latest.current) return;
        setData(result);
        setLoading(false);
      },
      (err) => {
        if (ticket !== latest.current) return;
        setError(err);
        setLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  return { data, error, loading, reload };
}

export function useDebounced<T>(value: T, delay = 280): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Closes a modal/dropdown on Escape — used by overlay primitives. */
export function useEscape(active: boolean, onEscape: () => void): void {
  useEffect(() => {
    if (!active) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onEscape();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, onEscape]);
}

/** Locks background scrolling while an overlay is open. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}
